import React, {useEffect, useMemo, useState} from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {Button, Space} from 'antd';
import {StyledTableFilters, StyledTableFiltersSelect} from './StyledTableFilters';
import ExportModal from '../../../../TransactionsPage/ExportModal';
import {StarsIcon} from '../../../../../../icons';
import {useIsMount} from '../../../../../../hooks';
import {fileHelpers, formHelpers} from '../../../../../../utils/helpers';
import {DEFAULT_MODAL_PROPS, expenseTypesConstants} from '../../../../../../constants';
import {expenseActions} from '../../../../../../state/actions/expense.actions';

const paymentOptions = {
  ALL: 'all',
  MISSING_INVOICES: 'missing_invoices',
}

const {ALL, MISSING_INVOICES} = paymentOptions;

const getPaymentStatusValue = (value) => ({
  has_invoice: value === MISSING_INVOICES ? false : undefined,
  expense_type: value === MISSING_INVOICES ? [expenseTypesConstants.INVOICE, expenseTypesConstants.RECEIPT] : undefined,
});

const getPaymentStatusFieldValue = (filter) => {
  if (filter.has_invoice === false) return MISSING_INVOICES;
  return ALL;
}

const selectProps = {
  allowClear: true,
  size: 'large',
  showSearch: true
}

const TableFilters = ({
  filterFields = ['search'],
  exportAdditionalFields = {},
  onFilter,

  employees,
  employeeEmail,
  exportExpenses,
  openExpenseAgentChat,
}) => {
  const [t] = useTranslation(['main', 'cards', 'transactions']);
  const [filter, setFilter] = useState({
    approver_email: undefined,
    has_invoice: undefined,
    reported_by: undefined,
    // search: '',
  });
  const [exportModalProps, setExportModalProps] = useState(DEFAULT_MODAL_PROPS);

  const isMounted = !useIsMount();

  useEffect(() => {
    if (isMounted) handleFilter(filter);
  }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const employeeOptions = useMemo(() =>
    formHelpers.getEmployeeOptions({employees, employeeEmail, t, valueKey: 'email'}), [employees, employeeEmail, t]);

  const paymentStatusOptions = [
    {label: t('all'), value: ALL},
    {label: t('missingInvoices'), value: MISSING_INVOICES},
  ];

  const paymentStatusValue = useMemo(() => getPaymentStatusFieldValue(filter), [filter]);

  const isEnabledField = (key) => filterFields.includes(key);

  const handleFilter = (filterData) => onFilter && onFilter(filterData);

  const handleOnFilter = (key, value) => setFilter({...filter, [key]: value});

  const handleChangePaymentStatus = (value) => {
    setFilter({
      ...filter,
      ...getPaymentStatusValue(value)
    });
  }

  const handleCancelExportModal = () => setExportModalProps({...exportModalProps, open: false, loading: false, error: null});

  const handleOpenExportModal = () => setExportModalProps({...exportModalProps, open: true});

  const handleOkExportModal = (data) => {

    const {date_range: dateRange} = data;
    const exportTimeFormat = 'YYYY-MM-DD';

    const isValidDateRange = dateRange && dateRange.length === 2;

    if (!isValidDateRange) return;

    const startDate = dateRange[0].format(exportTimeFormat);
    const endDate = dateRange[1].format(exportTimeFormat);

    const filterFields = Object.fromEntries(
      Object.entries(filter).filter(([_, value]) => value !== undefined && value !== null && value !== '')
    );

    const tableAdditionalFields = Object.fromEntries(
      Object.entries(exportAdditionalFields).filter(([key]) => !(key in filterFields))
    );

    const query = {
      start_date: startDate,
      end_date: endDate,
      ...tableAdditionalFields,
      ...filterFields,
    };

    setExportModalProps({...exportModalProps, loading: true});

    exportExpenses({
      query,
      successCallback: (data) => {
        fileHelpers.handleCSVExport(data, `expenses-${startDate}-${endDate}`);
        handleCancelExportModal();
      },
      errorCallback: (response) => {
        setExportModalProps({
          ...exportModalProps,
          error: response?.response?.data?.message || t('validation.exportError'),
          loading: false
        });
      }
    })
  }

  return (
    <StyledTableFilters align='center'>
      <Space align='center' size='small'>
        {isEnabledField('reported_by') && (
          <StyledTableFiltersSelect
            {...selectProps}
            styles={{ popup: { root: {minWidth: 235} } }}
            onChange={(value) => handleOnFilter('reported_by', value)}
            optionFilterProp='optionLabel'
            optionLabelProp='optionLabel'
            options={employeeOptions}
            placeholder={t('user')}
            value={filter?.reported_by}
          />
        )}
        {isEnabledField('approver') && (
          <StyledTableFiltersSelect
            {...selectProps}
            styles={{ popup: { root: {minWidth: 235} } }}
            onChange={(value) => handleOnFilter('approver_email', value)}
            optionFilterProp='optionLabel'
            optionLabelProp='optionLabel'
            options={employeeOptions}
            placeholder={t('approver')}
            value={filter?.approver_email}
          />
        )}
        {isEnabledField('status') && (
          <StyledTableFiltersSelect
            onChange={handleChangePaymentStatus}
            options={paymentStatusOptions}
            size='large'
            value={paymentStatusValue}
          />
        )}
      </Space>

      <Space size='small'>
        <Button
          onClick={handleOpenExportModal}
          size='large'
        >
          {t('export')}
        </Button>
        <Button
          icon={<StarsIcon />}
          onClick={openExpenseAgentChat}
          size='large'
          type='primary'
        >
          + {t('expense')}
        </Button>

      </Space>

      <ExportModal
        {...exportModalProps}
        options={['csv']}
        enableFormatField={false}
        handleCancel={handleCancelExportModal}
        handleOk={handleOkExportModal}
        title={t('export')}
      />

    </StyledTableFilters>
  );
}

TableFilters.propTypes = {
  exportAdditionalFields: PropTypes.object,
  onFilter: PropTypes.func,
  searchParams: PropTypes.object,
}

const mapStateToProps = ({ user: { employee }, company: { employees } }) => ({
  employees,
  employeeEmail: employee?.email ?? null,
});

const mapDispatchToProps = {
  exportExpenses: expenseActions.exportExpenses,
  openExpenseAgentChat: expenseActions.openExpenseAgentChat
}

export default connect(mapStateToProps, mapDispatchToProps)(TableFilters);
